import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppAccess } from '../hooks/useAppAccess';
import { extractOrgIdFromPath } from '../utils/organizationUtils';
import { LoadingSpinner } from './LoadingSpinner';

interface AppProtectedRouteProps {
  children: React.ReactNode;
  appName: string;
  requiredLevel?: 'view' | 'edit' | 'admin';
}

/**
 * Enhanced ProtectedRoute that checks both authentication AND app access
 * Prevents users from manually accessing apps they don't have permission for
 */
const AppProtectedRoute: React.FC<AppProtectedRouteProps> = ({ 
  children, 
  appName, 
  requiredLevel = 'view' 
}) => {
  const { user, loading: authLoading } = useAuth();
  const { hasAccess, loading: accessLoading, error: accessError } = useAppAccess();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract organization ID from current URL
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);

  // Show loading state while checking authentication and access
  if (authLoading || accessLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If no organization ID in URL, redirect to organization selection
  if (!orgIdFromPath || orgIdFromPath === 'default') {
    console.log(`🚫 No organization context found for ${appName} app, redirecting to organization selection`);
    return <Navigate to="/select-organization" replace />;
  }

  // Check if there's an error in access checking
  if (accessError) {
    console.error(`❌ Error checking access for ${appName}:`, accessError);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-2">Access Error</div>
          <div className="text-sm text-gray-500">Unable to verify app access permissions.</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if user has access to the specific app
  if (!hasAccess(appName)) {
    console.log(`🚫 User does not have access to ${appName} app for organization ${orgIdFromPath}`);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-2">Access Denied</div>
          <div className="text-sm text-gray-500 mb-4">
            Your current selected organization does not have access to this part of the application. 
            Go back to dashboard or change the organization and try again.
          </div>
          <div className="space-x-4">
            <button 
              onClick={() => navigate('/select-organization')} 
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Switch Organization
            </button>
            <button 
              onClick={() => navigate(`/o/${orgIdFromPath}`)} 
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // TODO: Add access level checking if needed
  // For now, we only check if user has access to the app
  // In the future, we can check specific access levels (view, edit, admin)

  // Render children if all checks pass
  return <>{children}</>;
};

export default AppProtectedRoute;
